import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from "@ngx-translate/core";
import axios from "axios";
import * as L from "leaflet";
import { CentrosViewModel } from "src/app/_core/models/centros/centro.model";
import { DebounceService } from "src/app/_core/providers/debounce.service";

/** Resultado de geocoding (Nominatim, el MISMO servicio ya usado para el reverse). */
interface GeoResult {
	lat: string;
	lon: string;
	display_name: string;
	type?: string;
}

/**
 * Mapa de ubicación del centro (Localización, ui-redesign).
 * - Lectura: pin fijo + readout compacto de coordenadas.
 * - Edición: pin arrastrable + buscador de texto (forward geocoding con Nominatim,
 *   sin servicios nuevos: ya se usaba para el reverse) + recentrar.
 * - El form (latitud/longitud/ciudad) es la fuente de verdad: el pin lo actualiza
 *   con SUS coordenadas exactas (el reverse solo aporta la ciudad) y la edición
 *   manual de lat/long mueve el pin.
 */
@Component({
	selector: "app-centro-map-card",
	templateUrl: "./centro-map-card.component.html",
	styleUrls: ["./centro-map-card.component.scss"],
	standalone: true,
	imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
})
export class CentroMapCardComponent
	implements AfterViewInit, OnChanges, OnDestroy
{
	@Input() centro: CentrosViewModel;
	@Input() detailFormGroup: FormGroup;
	@Input() editing = false;

	map: L.Map | null = null;
	private marker: L.Marker | null = null;
	private formSub: { unsubscribe(): void } | null = null;
	/** Evita el bucle pin→form→pin al sincronizar. */
	private syncing = false;

	searchText = "";
	searching = false;
	results: GeoResult[] = [];
	showResults = false;
	/** Coordenadas detectadas en el texto del buscador ("lat, long" pegado a mano). */
	coordCandidate: { lat: number; lng: number } | null = null;

	private readonly searchDebounced: () => void;

	constructor(
		private readonly _debounceService: DebounceService,
		private readonly cdr: ChangeDetectorRef,
	) {
		this.searchDebounced = this._debounceService.debounce(
			() => this.runSearch(),
			450,
		) as () => void;
	}

	// ── Lectura del estado actual ──
	get lat(): number | null {
		const v = parseFloat(this.detailFormGroup?.get("latitud")?.value);
		return Number.isFinite(v) ? v : null;
	}
	get lng(): number | null {
		const v = parseFloat(this.detailFormGroup?.get("longitud")?.value);
		return Number.isFinite(v) ? v : null;
	}
	get coordsLabel(): string {
		return this.lat != null && this.lng != null
			? `${this.lat.toFixed(5)}, ${this.lng.toFixed(5)}`
			: "—";
	}
	get ciudadLabel(): string {
		return this.detailFormGroup?.get("ciudad")?.value || "";
	}

	// ── Ciclo de vida ──
	ngAfterViewInit(): void {
		this.initMap();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["editing"] && this.marker) {
			this.applyDraggable();
		}
	}

	ngOnDestroy(): void {
		this.formSub?.unsubscribe();
		this.map?.remove();
		this.map = null;
	}

	// ── Mapa ──
	private initMap(): void {
		const el = document.getElementById("map");
		if (this.map || !el) {
			return;
		}
		const hasCoords = this.lat != null && this.lng != null;
		const center: L.LatLngExpression = hasCoords
			? [this.lat as number, this.lng as number]
			: [40.4168, -3.7038]; // fallback neutro (no 0,0 en medio del océano)
		const zoom = hasCoords ? 14 : 5;
		this.map = L.map("map", {
			center,
			zoom,
			zoomControl: false,
			scrollWheelZoom: true,
		});
		this.map.setView(this.visualCenter(center, zoom), zoom, {
			animate: false,
		});
		L.control.zoom({ position: "bottomright" }).addTo(this.map);
		L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
			attribution: "© OpenStreetMap contributors",
			maxZoom: 19,
		}).addTo(this.map);

		this.marker = L.marker(center, {
			icon: this.pinIcon(),
			draggable: false,
			autoPan: true,
		}).addTo(this.map);
		if (this.centro?.descripcion) {
			this.marker.bindTooltip(this.centro.descripcion, {
				direction: "top",
				offset: [0, -34],
			});
		}
		this.applyDraggable();

		const debouncedDrag = this._debounceService.debounce(
			(event: L.DragEndEvent) => this.onDragEnd(event),
			400,
		);
		this.marker.on("dragend", (event) => debouncedDrag(event));

		// Edición manual de lat/long → mover el pin (sin re-disparar el form).
		this.formSub = this.detailFormGroup?.valueChanges.subscribe(() => {
			if (this.syncing) {
				return;
			}
			if (this.lat != null && this.lng != null && this.marker) {
				const pos = this.marker.getLatLng();
				if (
					Math.abs(pos.lat - this.lat) > 1e-7 ||
					Math.abs(pos.lng - this.lng) > 1e-7
				) {
					this.marker.setLatLng([this.lat, this.lng]);
				}
			}
		});

		// El mapa vive dentro de una card con tabs: invalidar tamaño al pintarse.
		setTimeout(() => this.map?.invalidateSize(), 150);
	}

	/** Altura del header translúcido que flota sobre el mapa (0 si no existe). */
	private headerOffsetPx(): number {
		const head = document
			.getElementById("map")
			?.closest(".card-map")
			?.querySelector<HTMLElement>(".sec-head");
		return head?.offsetHeight ?? 0;
	}

	/** Centro corregido para que el pin caiga en el centro VISIBLE del mapa
	 * (la franja libre bajo el header flotante). Solo desplaza la vista;
	 * el pin conserva sus coordenadas reales. */
	private visualCenter(
		latlng: L.LatLngExpression,
		zoom: number,
	): L.LatLngExpression {
		const offset = this.headerOffsetPx() / 2;
		if (!this.map || !offset) {
			return latlng;
		}
		const pt = this.map.project(latlng, zoom).subtract([0, offset]);
		return this.map.unproject(pt, zoom);
	}

	/** Pin SVG del design-system (accent), sin assets externos. */
	private pinIcon(): L.DivIcon {
		const svg = `
			<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
				<path d="M17 1C8.7 1 2 7.6 2 15.8 2 27 17 43 17 43s15-16 15-27.2C32 7.6 25.3 1 17 1z"
					fill="#E30613" stroke="#fff" stroke-width="2.4"/>
				<circle cx="17" cy="15.5" r="5.4" fill="#fff"/>
			</svg>`;
		return L.divIcon({
			className: "ctr-pin",
			html: svg,
			iconSize: [34, 44],
			iconAnchor: [17, 43],
			tooltipAnchor: [0, -34],
		});
	}

	private applyDraggable(): void {
		if (!this.marker) {
			return;
		}
		if (this.editing) {
			this.marker.dragging?.enable();
		} else {
			this.marker.dragging?.disable();
			this.closeResults();
		}
	}

	/** El pin manda: el form recibe las coordenadas EXACTAS del pin; el reverse
	 * geocoding solo aporta la ciudad (antes pisaba lat/long con las del objeto). */
	private async onDragEnd(event: L.DragEndEvent): Promise<void> {
		const pos = (event.target as L.Marker).getLatLng();
		this.setCoords(pos.lat, pos.lng);
		await this.fillCiudad(pos.lat, pos.lng);
		this.cdr.markForCheck();
	}

	private setCoords(lat: number, lng: number): void {
		this.syncing = true;
		const latCtrl = this.detailFormGroup?.get("latitud");
		const lngCtrl = this.detailFormGroup?.get("longitud");
		latCtrl?.setValue(lat.toFixed(6));
		lngCtrl?.setValue(lng.toFixed(6));
		latCtrl?.markAsDirty();
		lngCtrl?.markAsDirty();
		this.syncing = false;
	}

	private async fillCiudad(lat: number, lng: number): Promise<void> {
		try {
			const response = await axios.get(
				`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=es`,
			);
			const a = response.data?.address ?? {};
			const ciudad = a.city || a.town || a.village || a.suburb || "";
			if (ciudad) {
				const ctrl = this.detailFormGroup?.get("ciudad");
				ctrl?.setValue(ciudad);
				ctrl?.markAsDirty();
			}
		} catch {
			// Silencioso: la ciudad es un extra, las coordenadas ya están fijadas.
		}
	}

	/**
	 * Sitúa mapa + pin + form en unas coordenadas (uso EXTERNO: autocompletar desde la
	 * referencia de ubicación seleccionada). Re-centra y rellena la ciudad. El contenedor
	 * decide si esto cuenta o no como selección manual.
	 */
	async irACoordenadas(lat: number, lng: number, zoom = 13): Promise<void> {
		if (!this.map || !this.marker || isNaN(lat) || isNaN(lng)) {
			return;
		}
		this.setCoords(lat, lng);
		this.marker.setLatLng([lat, lng]);
		this.map.setView(this.visualCenter([lat, lng], zoom), zoom, {
			animate: true,
		});
		await this.fillCiudad(lat, lng);
		this.cdr.markForCheck();
	}

	// ── Buscador (forward geocoding, mismo Nominatim del reverse) ──
	onSearchInput(value: string): void {
		this.searchText = value;
		if (!value || value.trim().length < 3) {
			this.closeResults();
			return;
		}
		// "lat, long" pegado a mano → opción local instantánea, sin red.
		this.coordCandidate = this.parseCoords(value);
		if (this.coordCandidate) {
			this.results = [];
			this.searching = false;
			this.showResults = true;
			return;
		}
		this.searching = true;
		this.showResults = true;
		this.searchDebounced();
	}

	/**
	 * Detecta un par de coordenadas en texto libre: separador coma/punto y coma/espacio,
	 * decimales con punto o coma ("39.4699, -0.3763" / "39,4699; -0,3763").
	 */
	private parseCoords(raw: string): { lat: number; lng: number } | null {
		const text = (raw ?? "").trim();
		let parts = text.split(/\s*;\s*/);
		if (parts.length !== 2) {
			parts = text.split(/\s*,\s*/);
		}
		if (parts.length !== 2) {
			parts = text.split(/\s+/);
		}
		// Caso "39,4699, -0,3763": 4 trozos separados por coma → reagrupar de dos en dos.
		if (parts.length === 4 && text.includes(",")) {
			parts = [`${parts[0]}.${parts[1]}`, `${parts[2]}.${parts[3]}`];
		}
		if (parts.length !== 2) {
			return null;
		}
		const toNum = (s: string): number => {
			const t = s.trim();
			// Coma decimal solo si no hay punto y hay UNA coma.
			const normalized =
				!t.includes(".") && (t.match(/,/g) || []).length === 1
					? t.replace(",", ".")
					: t;
			return /^[+-]?\d+(\.\d+)?$/.test(normalized)
				? parseFloat(normalized)
				: NaN;
		};
		const lat = toNum(parts[0]);
		const lng = toNum(parts[1]);
		if (
			!Number.isFinite(lat) ||
			!Number.isFinite(lng) ||
			Math.abs(lat) > 90 ||
			Math.abs(lng) > 180
		) {
			return null;
		}
		return { lat, lng };
	}

	/** Ir a unas coordenadas pegadas en el buscador: mueve pin + form + vuela. */
	goToCoordCandidate(): void {
		if (!this.coordCandidate) {
			return;
		}
		const { lat, lng } = this.coordCandidate;
		this.marker?.setLatLng([lat, lng]);
		const zoomTo = Math.max(this.map?.getZoom() ?? 15, 15);
		this.map?.flyTo(this.visualCenter([lat, lng], zoomTo), zoomTo, {
			duration: 0.8,
		});
		this.setCoords(lat, lng);
		void this.fillCiudad(lat, lng);
		this.searchText = "";
		this.closeResults();
	}

	/** Ubicación pendiente (lat/long inválidos): aviso superpuesto en edición. */
	get coordsInvalid(): boolean {
		return (
			!!this.detailFormGroup &&
			(this.detailFormGroup.get("latitud")?.invalid ||
				this.detailFormGroup.get("longitud")?.invalid)
		);
	}

	private async runSearch(): Promise<void> {
		const q = this.searchText.trim();
		if (q.length < 3) {
			this.searching = false;
			return;
		}
		try {
			const response = await axios.get(
				`https://nominatim.openstreetmap.org/search?format=json&limit=6&accept-language=es&q=${encodeURIComponent(q)}`,
			);
			this.results = (response.data ?? []) as GeoResult[];
		} catch {
			this.results = [];
		}
		this.searching = false;
		this.showResults = true;
		this.cdr.markForCheck();
	}

	selectResult(r: GeoResult): void {
		const lat = parseFloat(r.lat);
		const lng = parseFloat(r.lon);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			return;
		}
		this.marker?.setLatLng([lat, lng]);
		const zoomTo = Math.max(this.map?.getZoom() ?? 15, 15);
		this.map?.flyTo(this.visualCenter([lat, lng], zoomTo), zoomTo, {
			duration: 0.8,
		});
		this.setCoords(lat, lng);
		void this.fillCiudad(lat, lng);
		this.searchText = "";
		this.closeResults();
	}

	closeResults(): void {
		this.showResults = false;
		this.results = [];
		this.coordCandidate = null;
		this.searching = false;
	}

	/** Recentrar el mapa en el pin (centro visual, bajo el header flotante). */
	recenter(): void {
		if (this.marker && this.map) {
			const zoomTo = Math.max(this.map.getZoom(), 14);
			this.map.flyTo(this.visualCenter(this.marker.getLatLng(), zoomTo), zoomTo, {
				duration: 0.6,
			});
		}
	}
}
